import React from "react";
import {
  Box,
  Typography,
  useTheme,
  IconButton,
  Button,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import dayjs from "dayjs";
import { useStateContext } from "contexts/ContextProvider";
import { usePageTitle } from "hooks/usePageTitle";
import api from "services/api";
import { OVERLAY_NAMES } from "constants/Overlays";
import { formatExpenseDate } from "./expenseDateUtils";

const ExpenseProfile = () => {
  usePageTitle("Expense Details");

  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const {
    overlayData,
    setOverlayName,
    openDialog,
    closeDialog,
    showSnackbar,
    handleErrorDialog,
    setOverlayData,
  } = useStateContext();

  const closeOverlay = () => setOverlayName("");

  const expenses = overlayData?.expenses || [];
  const category = overlayData?.category || "Uncategorized";
  const subcategory = overlayData?.subcategory || "General";
  const monthYear = overlayData?.monthYear || "";
  const selectedYear = overlayData?.selectedYear || String(dayjs().year());
  const onSaved = overlayData?.onSaved;

  const handleEdit = (expense) => {
    setOverlayData({
      onSaved,
      selectedYear,
      expense,
    });
    setOverlayName(OVERLAY_NAMES.MANAGEMENT_EXPENSE_NEW_EDIT);
  };

  const handleDelete = (expense) => {
    openDialog({
      theme: theme,
      title: "Delete Expense",
      message: `Are you sure you want to delete this expense dated ${formatExpenseDate(expense.ExpenseDate)} for $${parseFloat(expense.Amount).toLocaleString()}?`,
      primaryAction: "Delete",
      secondaryAction: "Cancel",
      primaryOnClick: async () => {
        try {
          await api.delete(`/expenses/${expense.id}`);
          if (onSaved) {
            await onSaved();
          }
          closeDialog();
          showSnackbar("Expense deleted successfully", theme, "success");
          closeOverlay();
        } catch (error) {
          console.error("Error deleting expense:", error);
          handleErrorDialog(`Error deleting expense: ${error.message}`, theme);
        }
      },
      secondaryOnClick: () => {
        closeDialog();
      },
    });
  };

  const totalAmount = expenses.reduce(
    (sum, exp) => sum + (parseFloat(exp.Amount) || 0),
    0,
  );

  return (
    <Box
      onClick={(e) => e.stopPropagation()}
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        bgcolor: "rgba(0, 0, 0, 0.8)",
        zIndex: 1300,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Box
        sx={{
          bgcolor: theme.palette.background.alt,
          width: { xs: "100%", sm: "90%", md: "75%", lg: "60%" },
          maxHeight: "75%",
          minWidth: { xs: "100%", sm: "600px" },
          maxWidth: "1200px",
          display: "flex",
          borderRadius: "1.5rem",
          flexDirection: "column",
          color: theme.palette.text.primary,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            p: ".75rem 2rem",
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <IconButton color="primary" size="large" onClick={closeOverlay}>
              <ChevronLeftRoundedIcon fontSize="inherit" />
            </IconButton>
            <Typography variant={isMobile ? "h4" : "h2"} fontWeight="bold">
              Expense Details
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 0 }} />

        {/* Content */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            px: 4,
            py: 3,
          }}
        >
          {/* Category Chips */}
          <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
            <Chip
              label={category}
              color="primary"
              sx={{
                fontWeight: 600,
                fontSize: "0.875rem",
              }}
            />
            <Chip
              label={subcategory}
              variant="outlined"
              sx={{
                fontWeight: 500,
                fontSize: "0.875rem",
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.text,
              }}
            />
            <Chip
              label={monthYear}
              variant="outlined"
              sx={{
                fontWeight: 500,
                fontSize: "0.875rem",
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.text,
              }}
            />
          </Stack>

          {/* Expenses Table */}
          <TableContainer
            sx={{
              bgcolor: theme.palette.background.default,
              borderRadius: "12px",
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      color: theme.palette.primary.main,
                      bgcolor: theme.palette.background.alt,
                    }}
                  >
                    Date
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      color: theme.palette.primary.main,
                      bgcolor: theme.palette.background.alt,
                    }}
                  >
                    Description
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: 700,
                      color: theme.palette.primary.main,
                      bgcolor: theme.palette.background.alt,
                    }}
                  >
                    Amount
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      color: theme.palette.primary.main,
                      bgcolor: theme.palette.background.alt,
                    }}
                  >
                    Payment Method
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      color: theme.palette.primary.main,
                      bgcolor: theme.palette.background.alt,
                    }}
                  >
                    Reference #
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 700,
                      color: theme.palette.primary.main,
                      bgcolor: theme.palette.background.alt,
                    }}
                  >
                    Status
                  </TableCell>
                  <TableCell
                    align="center"
                    width="100px"
                    sx={{
                      fontWeight: 700,
                      color: theme.palette.primary.main,
                      bgcolor: theme.palette.background.alt,
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {expenses.map((expense) => (
                  <TableRow
                    key={expense.id}
                    hover
                    sx={{
                      "&:hover": {
                        bgcolor: theme.palette.action.hover,
                      },
                      "& .MuiTableCell-root": {
                        borderColor: theme.palette.divider,
                      },
                    }}
                  >
                    <TableCell sx={{ color: theme.palette.primary.text }}>
                      {formatExpenseDate(expense.ExpenseDate)}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: theme.palette.primary.text,
                        maxWidth: 300,
                        whiteSpace: "normal",
                        wordWrap: "break-word",
                      }}
                    >
                      {expense.Description || "N/A"}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        color: theme.palette.secondary[400],
                        fontWeight: 600,
                      }}
                    >
                      ${parseFloat(expense.Amount).toLocaleString()}
                    </TableCell>
                    <TableCell sx={{ color: theme.palette.primary.text }}>
                      {expense.PaymentMethod || "N/A"}
                    </TableCell>
                    <TableCell sx={{ color: theme.palette.primary.text }}>
                      {expense.ReferenceNumber || "N/A"}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={expense.Status || "Draft"}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          minWidth: 90,
                          color:
                            (expense.Status || "Draft") === "Finalized"
                              ? theme.palette.success.dark
                              : theme.palette.warning.dark,
                          backgroundColor:
                            (expense.Status || "Draft") === "Finalized"
                              ? theme.palette.success.light
                              : theme.palette.warning.light,
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box
                        sx={{
                          display: "flex",
                          gap: 0.5,
                          justifyContent: "center",
                        }}
                      >
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(expense)}
                            sx={{
                              color: theme.palette.primary.main,
                              "&:hover": {
                                bgcolor: theme.palette.action.hover,
                              },
                            }}
                          >
                            <EditRoundedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(expense)}
                            sx={{
                              color: theme.palette.error.main,
                              "&:hover": {
                                bgcolor: theme.palette.action.hover,
                              },
                            }}
                          >
                            <DeleteRoundedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Total Section */}
          <Box
            sx={{
              mt: 3,
              pt: 2,
              borderTop: `2px solid ${theme.palette.divider}`,
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.primary.text,
                }}
              >
                Total ({expenses.length} expense
                {expenses.length !== 1 ? "s" : ""}):
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: theme.palette.secondary[400],
                }}
              >
                $
                {totalAmount.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </Typography>
            </Stack>
          </Box>
        </Box>

        <Divider />

        {/* Footer */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            p: "1rem 2rem",
            gap: 2,
            flexShrink: 0,
          }}
        >
          <Button
            onClick={closeOverlay}
            variant="outlined"
            sx={{
              color: theme.palette.primary.main,
              borderColor: theme.palette.primary.main,
              fontWeight: 600,
              px: 3,
              "&:hover": {
                borderColor: theme.palette.primary.buttonHoverSelected,
                bgcolor: theme.palette.primary.buttonHoverSelected,
                color: theme.palette.background.default,
              },
            }}
          >
            Close
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default ExpenseProfile;
